import { Injectable } from '@angular/core';
import { Router} from '@angular/router';
import {
  HttpRequest,
  HttpErrorResponse,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AuthService } from '../../_services';
import { LOCAL_RECOVERABLE_ERRORS } from './local-response-error.context';
import { PLANNING_REQUEST, type PlanningRequestContext } from './planning-request.context';

/**
 * Requête du module Planning v2 (SPEC planning v2 §5.1) : /api/v1/plannings/…, /api/v1/ce-projects/<id>/planning (suivi de
 * « / », « ? » ou fin) et /api/v1/public/planning-shares/….
 */
export function isPlanningApiUrl(url: string): boolean {
  return /^(?:https?:\/\/[^/]+)?\/api\/v1\/(?:plannings\/|ce-projects\/[^/?#]+\/planning(?:[/?]|$)|public\/planning-shares\/)/.test(url);
}

/**
 * Requête ouverte par le code d'une offre (entreprise sans compte, AG-2135) : /api/v1/dpgf-pricings/<id>/…,
 * /api/v2/dpgfs-pricing/<id>/generate-…-pdf/<code>, /api/v1/generateZIP/<id>?code=… et
 * /api/v2/dpgfs/<id>/generate-artisan-chiffrage-excel. Après 5 codes refusés, le serveur y répond 429.
 */
export function isOfferCodeApiUrl(url: string): boolean {
  return /^(?:https?:\/\/[^/]+)?\/?api\/v[12]\/(?:dpgf-pricings\/[^/?#]+\/|dpgfs-pricing\/[^/?#]+\/generate-|generateZIP\/[^/?#]+\?(?:[^#]*&)?code=|dpgfs\/[^/?#]+\/generate-artisan-chiffrage-excel(?:[?#]|$))/.test(url);
}

/**
 * Corps JSON d'une erreur reçue par un téléchargement (responseType 'blob') : Angular le laisse en Blob « application/json »,
 * par exemple le 403 « Only demo » de l'Analyse des offres en Excel (generate-summary, AG-2008). Renvoie l'objet lu, ou
 * undefined si le corps n'est pas un Blob JSON lisible.
 */
export function readJsonErrorBlob(body: unknown): Promise<unknown> | undefined {
  if (typeof Blob === 'undefined' || !(body instanceof Blob) || !/[/+]json\b/i.test(body.type)) return undefined;
  return Promise.resolve().then(() => body.text()).then((text) => JSON.parse(text)).catch(() => undefined);
}

@Injectable()
export class GlobalResponseInterceptorInterceptor implements HttpInterceptor {

  constructor(
    private router:Router,
    private authService: AuthService
  ) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
      let currentRoute = window.location.pathname;
      var res = next.handle(request);
      let apiUrl = request.url;
      let apiMethod = request.method;
      
      return res.pipe(
        catchError((error: HttpErrorResponse) => {
          if (request.context.get(LOCAL_RECOVERABLE_ERRORS) && (error.status === 0 || error.status >= 500)) {
            return throwError(error);
          }
          // A download gets its JSON error body as a Blob (AG-2008): the rules below read it as for a JSON request, the
          // caller still receives the original error
          const jsonBlob = readJsonErrorBlob(error?.error);
          if (jsonBlob) {
            return from(jsonBlob).pipe(mergeMap((body) => {
              const readable = body === undefined ? error : { ...error, error: body } as HttpErrorResponse;
              return this.handleError(readable, currentRoute, apiUrl, apiMethod, request) ? EMPTY : throwError(error);
            }));
          }
          let isHandled = this.handleError(error, currentRoute, apiUrl, apiMethod, request); // Handle navigation based on error status
          // RxJS 6: throwError takes the error value directly (the () => error factory
          // syntax is RxJS 7+ and would emit the function itself instead of the error)
          return isHandled ? EMPTY : throwError(error); 
        })
      );
    }
  
    private handleError(error: HttpErrorResponse, currentRoute: string, apiUrl:string, requestMethod:string, request?: HttpRequest<unknown>): boolean {
      // A backend branch without deployment metadata must not interrupt the application.
      if (apiUrl === '/api/v1/demo-deployment') return false;
      // AuthService always clears the local session, including when logout fails.
      if (apiUrl.includes('/v1/auth/logout')) return false;
      // error message on 403      
      // console.log("handleError : ", error.error.message);

      // Public fiche d'appel d'offres: an anonymous page that renders its own
      // inline states. Every global redirect below (logout, /access-denied,
      // /**, /server-error) would eject the visitor off the fiche and hide the
      // reason, so let these errors reach the component untouched.
      if (apiUrl.includes("/v1/public/consultation/")) return false;
      // The agent panel keeps its draft and renders request failures in place.
      if (/\/ai\/agent(?:\/|$|\?)/.test(apiUrl) && error.status !== 401 && error.status !== 451) return false;
      // Keep deposit failures on the company page so the file can be selected again.
      if (/\/ce-project-invites\/[^/]+\/quote-documents(?:\/|$|\?)/.test(apiUrl) && error.status !== 401 && error.status !== 451) return false;
      // The internal comments panel (AG-1827) shows its failures in place: the Suivi des consultations stays open.
      if (/\/ce-projects\/[^/]+\/internal-comments(?:\/|$|\?)/.test(apiUrl) && error.status !== 401 && error.status !== 451) return false;
      // The messaging (AG-2033) shows its failures in place: the Suivi des consultations and the project page of the company stay open.
      if (/\/(?:ce-projects\/[^/]+\/(?:conversations|messaging)|ce-project-invites\/[^/]+\/conversation)(?:\/|$|\?)/.test(apiUrl)
        && error.status !== 401 && error.status !== 451) return false;
      // The windows « Relancer » (AG-1821) and « Envoi général » (AG-1755) show their failures in place: the Suivi des
      // consultations stays open.
      if (/\/(?:ce-projects|ce-project-invites)\/[^/]+\/(?:relances|act-mails)(?:\/|$|\?)/.test(apiUrl) && error.status !== 401 && error.status !== 451) return false;
      // The remarks of the alerts mail (AG-1904): the mail window of the MOE, and the pricing grid of the company (its remarks,
      // its answers), stay open and say so in place.
      if (/\/(?:ce-project-invites\/[^/]+|dpgf-pricings\/[^/]+\/[^/]+)\/alert-remarks(?:\/|$|\?)/.test(apiUrl) && error.status !== 401 && error.status !== 451) return false;
      // The Q/R (AG-1743) too: the fiche AO, the Suivi des consultations and the project page of the company stay open.
      if (/\/(?:ce-projects\/[^/]+\/questions(?:-setting)?|ce-project-invites\/[^/]+\/questions)(?:\/|$|\?)/.test(apiUrl)
        && error.status !== 401 && error.status !== 451) return false;
      // A custom frame deleted meanwhile by another user of the project (AG-1768): the analysis says so in place.
      if (/\/ce-projects\/[^/]+\/act-frames(?:\/|$|\?)/.test(apiUrl) && error.status === 404) return false;
      // A missing or unreadable preview stays in its dialog; access restrictions still apply.
      if (/\/ce-project-invites\/[^/]+\/complementary-documents\/preview(?:$|\?)/.test(apiUrl)
        && ![401, 403, 451].includes(error.status)) return false;
      // Too many wrong codes for this offer (AG-2135, 429 « Trop de codes erronés… »): no error page, the error reaches
      // the caller; the code page shows the server message.
      if (error.status === 429 && isOfferCodeApiUrl(apiUrl)) return false;
      // 'Dépôt après la date limite : Non autorisé': the page shows the server message.
      if (error.status === 403 && error?.error?.err?.code === 'LATE_SUBMISSION_FORBIDDEN') return false;
      // Company pricing refused (consultation not accepted, lot declined or attributed): the page stays open.
      if (error.status === 403 && ['INVITE_NOT_ACCEPTED', 'LOT_DECLINED', 'LOT_ATTRIBUTED'].includes(error?.error?.err?.code)) return false;
      // Planning v2 (§5.1): 451 stays global; 401 stays global unless the request carries PLANNING_REQUEST {handles401: true}
      // (pending commands pause instead of logging out); every other error reaches the caller, without redirection.
      if (isPlanningApiUrl(apiUrl) && error.status !== 451) {
        if (error.status !== 401) return false;
        const planning = request?.context?.get(PLANNING_REQUEST) as PlanningRequestContext | null | undefined;
        if (planning?.handles401 === true) return false;
      }

      // when an error occurs on suggestions api don't go to errors page, just throw error on console
      let isSuggestionsAPI = apiUrl.includes("/cstb-suggestions") && requestMethod == "POST";      
      const errorMessage = typeof error.error === 'string' ? error.error : error?.error?.message;

      if (error?.status === 451) {
        let userSessionLocation = {
          country: error.error["country"],
          city: error.error["city"],
        };
        localStorage.setItem("userSessionLocation", JSON.stringify(userSessionLocation));
        this.router.navigate(["/no-multi-users"], { replaceUrl: true });
        return true;
      // } else if (error.status === 500 && !isSuggestionsAPI && (error.error.err||{}).message!="You already reached maximum users" && !error?.error?.includes('Only PNG, JPEG, and JPG files')) {
      } else if(error?.status === 500 && !isSuggestionsAPI && (error?.error?.err||{}).message!="You already reached maximum users" && !errorMessage?.includes('Only PNG, JPEG, and JPG files')) {
        this.router.navigate(['/server-error'], { queryParams: { returnURL: currentRoute } });
        return true;
      } else if (error?.status === 502) {
        this.router.navigate(['/server-inaccessible'], { queryParams: { returnURL: currentRoute } });
        return true;
      } 
      //  else if (error.status === 403 && error?.error?.err?.message != 'login failed' && error?.error?.err?.message != "Only demo" && error?.error?.message!='User Not authorized to use cstb' && !error?.error?.err?.message.includes('You dont have the rights to view')) { 
      // wrong / expired code: let the 403 reach the component so it can show the
      // error inline (MFA step, artisan email-code step) instead of redirecting
      else if (error.status === 403 && (apiUrl.includes('/auth/mfa/verify') || apiUrl.includes('/auth/artisan-email-code/verify'))) {
        return false;
      }
      // signup refused by the invisible reCAPTCHA: the form shows the v2 checkbox inline
      else if (error.status === 403 && error?.error?.err?.captchaRequired === true) {
        return false;
      }
      else if (error.status === 403 && error?.error?.err?.message != 'login failed' && error?.error?.message != "Only demo" && error?.error?.message!='User Not authorized to use cstb' && !error?.error?.err?.message.includes('You dont have the rights to view')) { 
        this.router.navigate(['/access-denied'], { queryParams: { returnURL: '/' } });
        return true;
      } else if (error?.status === 404 && !apiUrl.includes("assets/version.json") && currentRoute!='/artisan/subscribe'  && error?.error?.err?.message!='Lot has not been found') {
        this.router.navigate(['/**'], { queryParams: { returnURL: '/' } });
        return true;
      } 
      // else if (error.status === 413) {
      //   this.router.navigate(['/entity-too-large'], { queryParams: { returnURL: currentRoute } });
      //   return true;
      // }
      else if(error?.status == 401 && !apiUrl.includes("/v1/auth/logout") && !currentRoute.includes("/artisan/dpgf/")){
        this.authService.logout()
        return true;
      }
      return false;
    }
 
}
